import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';
import { graphql, useMutation } from 'react-relay';
import type { RecordSourceSelectorProxy } from 'relay-runtime';

import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import { token } from '@atlaskit/tokens';

import type { TrackType } from '../../api/cc-smarts/models/TrackType';
import type { FlagsContainer } from '../ui/audio-player/AudioContainer';

import type { useContextMenuActionsAddTrackMutation } from './__generated__/useContextMenuActionsAddTrackMutation.graphql';
import type { useContextMenuActionsRemoveTrackMutation } from './__generated__/useContextMenuActionsRemoveTrackMutation.graphql';
import type {
	ConfluenceTrackInput,
	useContextMenuActionsReorderTrackMutation,
} from './__generated__/useContextMenuActionsReorderTrackMutation.graphql';
import { useAudioPlayerStore } from './useAudioPlayerStore';

const messages = defineMessages({
	reorderSuccessTitle: {
		id: 'ai-audio.playlist.reorder-success-description',
		defaultMessage: 'Moved {title}',
		description: 'Description shown when an item is reordered in the playlist',
	},
	reorderErrorDescription: {
		id: 'ai-audio.playlist.reorder-error-description',
		defaultMessage: 'Failed to move item in playlist',
		description: 'Description shown when an item fails to be reordered in the playlist',
	},
	removeSuccess: {
		id: 'ai-audio.playlist.remove-success',
		defaultMessage: 'Removed from playlist',
		description: 'Success message shown when an item is removed from the playlist',
	},
	errorTitle: {
		id: 'ai-audio.playlist.error',
		defaultMessage: 'Something went wrong',
		description: 'Error message shown when an error occurs.',
	},
	removeSuccessDescription: {
		id: 'ai-audio.playlist.remove-success-description',
		defaultMessage: '{title} has been removed from your playlist',
		description: 'Description shown when an item is removed from the playlist',
	},
	removeErrorDescription: {
		id: 'ai-audio.playlist.remove-error-description',
		defaultMessage: 'Failed to remove item from playlist',
		description: 'Description shown when an item fails to be removed from the playlist',
	},
	addSuccess: {
		id: 'ai-audio.playlist.add-success',
		defaultMessage: 'Added to playlist',
		description: 'Success message shown when an item is added from the playlist',
	},
	addError: {
		id: 'ai-audio.playlist.add-error',
		defaultMessage: 'Something went wrong',
		description: 'Error message shown when an item fails to be added to the playlist',
	},
	addSuccessDescription: {
		id: 'ai-audio.playlist.add-success-description',
		defaultMessage: 'Listen anywhere on Confluence or the mobile app',
		description: 'Description shown when an item is added to the playlist',
	},
	addErrorDescription: {
		id: 'ai-audio.playlist.add-error-description',
		defaultMessage: 'Failed to add item to playlist',
		description: 'Description shown when an item fails to be added to the playlist',
	},
});

/**
 * Helper function to check if a track node matches the given track parameters
 */
const isMatchingTrack = (
	node: any,
	mainSource: string,
	trackType: TrackType,
	supportingSources: string[],
): boolean => {
	if (node?.getValue('mainSource') !== mainSource || node?.getValue('type') !== trackType) {
		return false;
	}
	const nodeSupportingSources = (node?.getValue('supportingSources') as string[]) || [];
	// Check that arrays have the same length first
	if (nodeSupportingSources.length !== supportingSources.length) {
		return false;
	}
	// Then check that all elements match
	return nodeSupportingSources.every(
		(source: string, index: number) => source === supportingSources[index],
	);
};

export const useContextMenuActions = (flags?: FlagsContainer, argCloudId?: string) => {
	const { cloudId: storeCloudID } = useAudioPlayerStore();
	const { formatMessage } = useIntl();
	//consumers can call hooks even when the audio player is collapsed. In this case the audoPLayerStore won't exist, and they should bootstrap with their own cloudID.
	const cloudId = argCloudId || storeCloudID;

	const [removeTrackMutation, isRemoveMutationInFlight] =
		useMutation<useContextMenuActionsRemoveTrackMutation>(graphql`
			mutation useContextMenuActionsRemoveTrackMutation(
				$cloudId: ID!
				$input: ConfluenceTrackInput!
			) {
				confluence_removeTrack(cloudId: $cloudId, input: $input) {
					success
					errors {
						message
						extensions {
							statusCode
						}
					}
				}
			}
		`);

	const [reorderTrackMutation, isReorderMutationInFlight] =
		useMutation<useContextMenuActionsReorderTrackMutation>(graphql`
			mutation useContextMenuActionsReorderTrackMutation(
				$cloudId: ID!
				$input: ConfluenceReorderTrackInput!
			) {
				confluence_reorderTracks(cloudId: $cloudId, input: $input) {
					success
					errors {
						message
						extensions {
							statusCode
						}
					}
				}
			}
		`);

	const removeTrack = useCallback(
		(mainSource: string, supportingSources: string[], trackType: TrackType, title?: string) => {
			return new Promise<boolean>((resolve, reject) => {
				removeTrackMutation({
					variables: {
						cloudId,
						input: {
							mainSource,
							supportingSources,
							trackType,
						},
					},
					onCompleted: (response) => {
						const success = response.confluence_removeTrack?.success;
						const errors = response.confluence_removeTrack?.errors;

						if (success) {
							resolve(true);
							if (flags && title) {
								flags.showFlag({
									type: 'success',
									title: formatMessage(messages.removeSuccess),
									description: formatMessage(messages.removeSuccessDescription, {
										title,
									}),
									icon: (
										<StatusSuccessIcon
											spacing="spacious"
											color={token('color.icon.success')}
											label="success"
										/>
									),
									close: 'auto',
								});
							}
						} else {
							const errorMessage = errors?.[0]?.message || 'Failed to remove track';
							reject(new Error(errorMessage));

							if (flags) {
								flags.showFlag({
									title: formatMessage(messages.errorTitle),
									description: formatMessage(messages.removeErrorDescription),
									type: 'error',
									close: 'auto',
								});
							}
						}
					},
					onError: (error) => {
						reject(error);
						if (flags) {
							flags.showFlag({
								title: formatMessage(messages.errorTitle),
								description: formatMessage(messages.removeErrorDescription),
								type: 'error',
								close: 'auto',
							});
						}
					},
					updater: (store) => {
						// Update the Relay store to remove the track from the cache
						// This ensures the UI updates immediately without refetching
						const playlistRecord = store.getRoot().getLinkedRecord('confluence_getPlaylist', {
							cloudId,
						});
						if (playlistRecord) {
							const tracksRecord = playlistRecord.getLinkedRecord('tracks');
							if (tracksRecord) {
								const nodes = tracksRecord.getLinkedRecords('nodes');
								if (nodes) {
									const updatedNodes = nodes.filter(
										(node) => !isMatchingTrack(node, mainSource, trackType, supportingSources),
									);
									tracksRecord.setLinkedRecords(updatedNodes, 'nodes');
								}
							}
						}
					},
				});
			});
		},
		[removeTrackMutation, cloudId, flags, formatMessage],
	);

	const [addTrackMutation, isAddMutationInFlight] =
		useMutation<useContextMenuActionsAddTrackMutation>(graphql`
			mutation useContextMenuActionsAddTrackMutation(
				$cloudId: ID!
				$input: ConfluenceAddTrackInput!
			) {
				confluence_addTrack(cloudId: $cloudId, input: $input) {
					success
					errors {
						message
						extensions {
							statusCode
						}
					}
					track {
						id
						type
						mainSource
						supportingSources
						mainContent {
							id
							title
							type
							status
							subType
							space {
								key
							}
							history {
								createdBy {
									displayName
									profilePicture {
										path
									}
								}
							}
							properties(keys: ["emoji-title-published"]) {
								nodes {
									value
								}
							}
						}
					}
				}
			}
		`);

	const addTrack = useCallback(
		(mainSource: string, supportingSources: string[] = [], trackType: TrackType) => {
			return new Promise<boolean>((resolve, reject) => {
				addTrackMutation({
					variables: {
						cloudId,
						input: {
							track: {
								mainSource,
								supportingSources,
								trackType: trackType || 'BRIEFING',
							},
							top: true,
						},
					},
					onCompleted: (response: any) => {
						const success = response.confluence_addTrack?.success;
						const errors = response.confluence_addTrack?.errors;

						if (success) {
							resolve(true);
							flags?.showFlag({
								title: formatMessage(messages.addSuccess),
								description: formatMessage(messages.addSuccessDescription),
								icon: (
									<StatusSuccessIcon
										spacing="spacious"
										color={token('color.icon.success')}
										label="success"
									/>
								),
								type: 'success',
								close: 'auto',
							});
						} else {
							const errorMessage = errors?.[0]?.message || 'Failed to add track';
							reject(new Error(errorMessage));
							flags?.showFlag({
								title: formatMessage(messages.addError),
								description: formatMessage(messages.addErrorDescription),
								type: 'error',
								close: 'auto',
							});
						}
					},
					onError: () => {
						reject(new Error('Failed to add track'));
						flags?.showFlag({
							title: formatMessage(messages.addError),
							description: formatMessage(messages.addErrorDescription),
							type: 'error',
							close: 'auto',
						});
					},
					updater: (store: RecordSourceSelectorProxy) => {
						// Update the Relay store to add/move the track to the top of the playlist
						const payload = store.getRootField('confluence_addTrack');
						const newTrack = payload?.getLinkedRecord('track');
						if (!payload?.getValue('success') || !newTrack) {
							return;
						}

						const playlist = store.getRoot().getLinkedRecord('confluence_getPlaylist', {
							cloudId,
						});
						const tracks = playlist?.getLinkedRecord('tracks');
						if (!tracks) {
							return;
						}

						const nodes = tracks.getLinkedRecords('nodes') || [];

						// Check if the track already exists in the playlist
						const existingTrackIndex = nodes.findIndex((node) =>
							isMatchingTrack(node, mainSource, trackType, supportingSources),
						);

						let updatedNodes: any[];
						if (existingTrackIndex !== -1) {
							// Track exists: remove it from its current position and add to top
							const filteredNodes = nodes.filter(
								(node) => !isMatchingTrack(node, mainSource, trackType, supportingSources),
							);
							updatedNodes = [newTrack, ...filteredNodes];
						} else {
							// Track doesn't exist: just add to top
							updatedNodes = [newTrack, ...nodes];
						}

						tracks.setLinkedRecords(updatedNodes, 'nodes');
					},
				});
			});
		},
		[cloudId, addTrackMutation, flags, formatMessage],
	);

	/**
	 * • Target track - The track being repositioned
	 * • Reference track - The track that serves as the anchor point. The target track will be placed immediately after
	 *    the reference track. If no reference track is specified, the target track moves to the beginning of the playlist.
	 */
	const reorderTrack = useCallback(
		(targetTrack: ConfluenceTrackInput, referenceTrack?: ConfluenceTrackInput, title?: string) => {
			return new Promise<boolean>((resolve, reject) => {
				reorderTrackMutation({
					variables: {
						cloudId,
						input: {
							targetTrack,
							referenceTrack,
						},
					},
					onCompleted: (response) => {
						const success = response.confluence_reorderTracks?.success;
						const errors = response.confluence_reorderTracks?.errors;

						if (success) {
							resolve(true);
							if (flags && title) {
								flags.showFlag({
									title: formatMessage(messages.reorderSuccessTitle, { title }),
									icon: (
										<StatusSuccessIcon
											spacing="spacious"
											color={token('color.icon.success')}
											label="success"
										/>
									),
									close: 'auto',
								});
							}
						} else {
							const errorMessage = errors?.[0]?.message || 'Failed to reorder track';
							reject(new Error(errorMessage));

							if (flags) {
								flags.showFlag({
									title: formatMessage(messages.errorTitle),
									description: formatMessage(messages.reorderErrorDescription),
									type: 'error',
									close: 'auto',
								});
							}
						}
					},
					onError: (error) => {
						reject(error);
						if (flags) {
							flags.showFlag({
								title: formatMessage(messages.errorTitle),
								description: formatMessage(messages.reorderErrorDescription),
								type: 'error',
								close: 'auto',
							});
						}
					},
				});
			});
		},
		[reorderTrackMutation, cloudId, flags, formatMessage],
	);

	return {
		removeTrack,
		isRemoving: isRemoveMutationInFlight,
		isDisabled: isRemoveMutationInFlight || isReorderMutationInFlight,
		addTrack,
		isAdding: isAddMutationInFlight,
		reorderTrack,
	};
};
